import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';

const DATA = '/blog/comma-compression/data';
const HI_W = 1164;
const HI_H = 874;
const LO_W = 512;
const LO_H = 384;

const Wrapper = styled.div`
  margin: 2.5rem 0;
  padding: 1.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-alt);
`;

const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: start;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const PanelLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  text-transform: uppercase;
`;

const CanvasFrame = styled.div`
  border-radius: 4px;
  overflow: hidden;
  line-height: 0;
  background: #000;
`;

const Cnv = styled.canvas`
  width: 100%;
  height: auto;
  display: block;
`;

const Controls = styled.div`
  margin-top: 1.25rem;
`;

const SliderRow = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  gap: 0.75rem;
  align-items: center;
`;

const SliderLabel = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const PlayButton = styled.button<{ active: boolean }>`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--border);
  background: ${({ active }) => (active ? 'var(--accent)' : 'transparent')};
  color: ${({ active }) => (active ? '#fff' : 'var(--text-muted)')};
  border-radius: 3px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent);
    color: ${({ active }) => (active ? '#fff' : 'var(--accent)')};
  }
`;

const StyledSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  background: var(--border);
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    background: var(--accent);
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid var(--bg-alt);
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    background: var(--accent);
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid var(--bg-alt);
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }
`;

const Caption = styled.div`
  font-family: var(--font-body);
  font-size: 0.82rem;
  color: var(--text-muted);
  line-height: 1.55;
  margin-top: 0.85rem;
`;

/* Pull a float-pixel buffer (RGB only, 3 floats per pixel) out of an HTMLImageElement
   by drawing it to an offscreen canvas at native resolution and reading ImageData. */
function imageToFloat32(img: HTMLImageElement, w: number, h: number): Float32Array {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  ctx.drawImage(img, 0, 0, w, h);
  const id = ctx.getImageData(0, 0, w, h).data;
  const out = new Float32Array(w * h * 3);
  for (let i = 0, j = 0; i < w * h; i++, j += 3) {
    out[j + 0] = id[i * 4 + 0];
    out[j + 1] = id[i * 4 + 1];
    out[j + 2] = id[i * 4 + 2];
  }
  return out;
}

/* PyTorch-equivalent bilinear downsample with align_corners=False, antialias=False.
   Operates on float buffers and writes a Uint8ClampedArray RGBA suitable for putImageData. */
function bilinearDownsampleToImageData(
  srcF: Float32Array, srcW: number, srcH: number,
  dstW: number, dstH: number,
): Uint8ClampedArray {
  const dst = new Uint8ClampedArray(dstW * dstH * 4);
  const sx = srcW / dstW;
  const sy = srcH / dstH;
  for (let y = 0; y < dstH; y++) {
    let srcY = (y + 0.5) * sy - 0.5;
    if (srcY < 0) srcY = 0;
    if (srcY > srcH - 1) srcY = srcH - 1;
    const y0 = Math.floor(srcY);
    const y1 = Math.min(srcH - 1, y0 + 1);
    const wy = srcY - y0;
    const ny = 1 - wy;
    for (let x = 0; x < dstW; x++) {
      let srcX = (x + 0.5) * sx - 0.5;
      if (srcX < 0) srcX = 0;
      if (srcX > srcW - 1) srcX = srcW - 1;
      const x0 = Math.floor(srcX);
      const x1 = Math.min(srcW - 1, x0 + 1);
      const wx = srcX - x0;
      const nx = 1 - wx;
      const o00 = (y0 * srcW + x0) * 3;
      const o10 = (y0 * srcW + x1) * 3;
      const o01 = (y1 * srcW + x0) * 3;
      const o11 = (y1 * srcW + x1) * 3;
      const w00 = ny * nx, w10 = ny * wx, w01 = wy * nx, w11 = wy * wx;
      const di = (y * dstW + x) * 4;
      dst[di + 0] = srcF[o00] * w00 + srcF[o10] * w10 + srcF[o01] * w01 + srcF[o11] * w11;
      dst[di + 1] = srcF[o00 + 1] * w00 + srcF[o10 + 1] * w10 + srcF[o01 + 1] * w01 + srcF[o11 + 1] * w11;
      dst[di + 2] = srcF[o00 + 2] * w00 + srcF[o10 + 2] * w10 + srcF[o01 + 2] * w01 + srcF[o11 + 2] * w11;
      dst[di + 3] = 255;
    }
  }
  return dst;
}

/* Composite two float buffers with linear alpha blending, returning RGBA Uint8ClampedArray. */
function compositeToImageData(
  origF: Float32Array, pertF: Float32Array,
  blend: number, w: number, h: number,
): { imageData: Uint8ClampedArray; floatBuf: Float32Array } {
  const n = w * h;
  const imageData = new Uint8ClampedArray(n * 4);
  const floatBuf = new Float32Array(n * 3);
  const inv = 1 - blend;
  for (let i = 0, ji = 0, jf = 0; i < n; i++, ji += 4, jf += 3) {
    const r = origF[jf] * inv + pertF[jf] * blend;
    const g = origF[jf + 1] * inv + pertF[jf + 1] * blend;
    const b = origF[jf + 2] * inv + pertF[jf + 2] * blend;
    floatBuf[jf] = r;
    floatBuf[jf + 1] = g;
    floatBuf[jf + 2] = b;
    imageData[ji] = r;
    imageData[ji + 1] = g;
    imageData[ji + 2] = b;
    imageData[ji + 3] = 255;
  }
  return { imageData, floatBuf };
}

const NullspaceSlider: React.FC = () => {
  const [t, setT] = useState(0);
  const [auto, setAuto] = useState(false);
  const [ready, setReady] = useState(false);

  // Float32 RGB buffers loaded once, used to composite without 8-bit rounding
  const origFRef = useRef<Float32Array | null>(null);
  const pertFRef = useRef<Float32Array | null>(null);

  const leftCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const rightCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const rafRef = useRef<number | null>(null);
  const dirRef = useRef(1);

  // Load source images once and convert to Float32 buffers
  useEffect(() => {
    let cancelled = false;
    const loadImage = (src: string) => new Promise<HTMLImageElement>((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = src;
    });
    Promise.all([
      loadImage(`${DATA}/frame-original.png?v=2`),
      loadImage(`${DATA}/frame-perturbed.png?v=2`),
    ]).then(([orig, pert]) => {
      if (cancelled) return;
      origFRef.current = imageToFloat32(orig, HI_W, HI_H);
      pertFRef.current = imageToFloat32(pert, HI_W, HI_H);
      setReady(true);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Render: composite in Float32 (no 8-bit rounding at the blend step), draw to left,
  // then bilinear-downsample the float composite to the right using the same formula
  // PyTorch used to generate the perturbation (align_corners=False, antialias=False).
  useEffect(() => {
    if (!ready) return;
    const left = leftCanvasRef.current;
    const right = rightCanvasRef.current;
    const origF = origFRef.current;
    const pertF = pertFRef.current;
    if (!left || !right || !origF || !pertF) return;
    const lc = left.getContext('2d');
    const rc = right.getContext('2d');
    if (!lc || !rc) return;

    const blend = t / 100;

    const { imageData: leftRGBA, floatBuf } = compositeToImageData(origF, pertF, blend, HI_W, HI_H);
    lc.putImageData(new ImageData(leftRGBA, HI_W, HI_H), 0, 0);

    const rightRGBA = bilinearDownsampleToImageData(floatBuf, HI_W, HI_H, LO_W, LO_H);
    rc.putImageData(new ImageData(rightRGBA, LO_W, LO_H), 0, 0);
  }, [t, ready]);

  // Auto-play: bounce between 0 and 100
  useEffect(() => {
    if (!auto) return;
    const tick = () => {
      setT((prev) => {
        let next = prev + dirRef.current * 0.6;
        if (next >= 100) {
          next = 100;
          dirRef.current = -1;
        } else if (next <= 0) {
          next = 0;
          dirRef.current = 1;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [auto]);

  return (
    <Wrapper>
      <PanelGrid>
        <Panel>
          <PanelLabel>What you see (1164 × 874)</PanelLabel>
          <CanvasFrame>
            <Cnv ref={leftCanvasRef} width={HI_W} height={HI_H} aria-label="High-resolution composite blended between original and nullspace-perturbed frame" />
          </CanvasFrame>
        </Panel>
        <Panel>
          <PanelLabel>What the metric sees (512 × 384, computed live)</PanelLabel>
          <CanvasFrame>
            <Cnv ref={rightCanvasRef} width={LO_W} height={LO_H} aria-label="Bilinear-downsampled view of the left canvas, mirroring what the evaluator network receives" />
          </CanvasFrame>
        </Panel>
      </PanelGrid>

      <Controls>
        <SliderRow>
          <PlayButton active={auto} onClick={() => setAuto((v) => !v)}>
            {auto ? 'Pause' : 'Play'}
          </PlayButton>
          <SliderLabel>Original</SliderLabel>
          <StyledSlider
            type="range"
            min={0}
            max={100}
            step={0.5}
            value={t}
            onChange={(e) => { setAuto(false); setT(parseFloat(e.target.value)); }}
            aria-label="Blend between original and nullspace-perturbed image"
          />
          <SliderLabel>Perturbed</SliderLabel>
        </SliderRow>
      </Controls>

      <Caption>
        The right panel is computed live: it&apos;s a bilinear downsample of whatever&apos;s
        currently on the left, redrawn on every slider tick. The left panel morphs
        between two visibly different 1164×874 images (one is the original frame, the
        other is the same frame plus a heavy noise field). The right panel barely
        moves a pixel, because both versions of the high-res image bilinear-downsample
        to (essentially) the same 512×384 view.
      </Caption>
    </Wrapper>
  );
};

export default NullspaceSlider;
